"use client";

import React from "react";
import { Activity, FlaskConical, Layers, Cpu, ShieldCheck, Wrench, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const categoryItems = [{
  id: "spectroscopy",
  title: "NIR & Raman Spectroscopy",
  tag: "Spectroscopy",
  description: "Handheld Raman analyzers and in-line NIR process spectrometers for raw material identification and molecular analysis.",
  iconName: "Activity",
  linkPath: "/NirProcessSpectrometersPage",
  featuredProduct: "VIAVI MicroNIR & Rigaku ResQ"
}, {
  id: "chromatography",
  title: "Chromatography & Solvent Recovery",
  tag: "Chromatography",
  description: "Preparative and analytical chromatography solutions alongside sustainable solvent recycling systems for laboratory workflows.",
  iconName: "FlaskConical",
  linkPath: "/ChromatographyPage",
  featuredProduct: "ReSolve HPLC Recycling"
}, {
  id: "powder-testing",
  title: "Powder & Tablet Characterization",
  tag: "Physical Testing",
  description: "Flowability testers, tapped density volumeters, and tablet testing instrumentation compliant with pharmacopeial standards.",
  iconName: "Layers",
  linkPath: "/PowderTestingPage",
  featuredProduct: "Copley PTW & JV Series"
}, {
  id: "pat",
  title: "Process Analytical Technology (PAT)",
  tag: "In-Line Monitoring",
  description: "Real-time process monitoring probes and chemometric integration for blend uniformity, granulating, and drying endpoints.",
  iconName: "Cpu",
  linkPath: "/ProcessAnalyticalTechnologyPage",
  featuredProduct: "Matrix-F II & MicroNIR PAT"
}, {
  id: "safety-security",
  title: "Safety, Security & Narcotics ID",
  tag: "Field & Lab Analysis",
  description: "Ruggedized 1064nm Raman spectrometers and trace explosive detection platforms for rapid forensic and field screening.",
  iconName: "ShieldCheck",
  linkPath: "/SafetyAndSecurityPage",
  featuredProduct: "CQL Gen ID & ResQ CQL"
}, {
  id: "lifecycle-services",
  title: "Qualification & Lifecycle Services",
  tag: "Compliance & Support",
  description: "DQ, IQ, OQ, PQ validation protocols, chemometric model building, preventive maintenance, and laboratory staff training.",
  iconName: "Wrench",
  linkPath: "/QualificationLifecycleServicesPage",
  featuredProduct: "ISO & GAMP Compliance"
}];
export default function Categories() {
  const prefersReducedMotion = useReducedMotion();
  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case "Activity":
        return <Activity className="size-6 text-primary" aria-hidden="true" />;
      case "FlaskConical":
        return <FlaskConical className="size-6 text-primary" aria-hidden="true" />;
      case "Layers":
        return <Layers className="size-6 text-primary" aria-hidden="true" />;
      case "Cpu":
        return <Cpu className="size-6 text-primary" aria-hidden="true" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" aria-hidden="true" />;
      default:
        return <Activity className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="productcategoriesgrid" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <header className="mb-12 md:mb-16 max-w-3xl">
          <Badge variant="secondary" className="mb-3">
            Analytical Instrumentation
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
            Product Families & Laboratory Technologies
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-default">
            Application-focused instrumentation, physical testing systems, and qualification services engineered for regulated analytical laboratories and process manufacturing.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categoryItems.map((category, index) => <motion.div key={category.id} data-index={index} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: index * 0.08
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card hover:border-primary/40 transition-colors shadow-sm">
                <div>
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-secondary text-primary inline-flex items-center justify-center">
                        {getCategoryIcon(category.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {category.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold font-default text-card-foreground">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground font-default">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="rounded-md bg-secondary/60 px-3 py-2 text-xs text-secondary-foreground font-default">
                      <span className="font-medium text-foreground">Key Systems: </span>
                      {category.featuredProduct}
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border/60">
                  <Button variant="ghost" size="sm" asChild className="w-full justify-between hover:bg-secondary hover:text-foreground group px-2">
                    <Link to={category.linkPath} className="flex items-center justify-between w-full">
                      <span>Explore Category</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 text-primary" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        <div className="mt-12 text-center">
          <Button variant="default" size="lg" asChild>
            <Link to="/ProductsPage">
              View All Products & Systems
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}